import type { AgentSettings } from "@repo/agent-config";
import {
	type AppearanceOverrides,
	CHAT_LABELS,
	type ChatLabel,
	type ChatText,
} from "@repo/embed";
import { COPY } from "./copy";

/**
 * What the retailer's app changed in the chat it opened (ADR-5.1, 5.4), laid over the
 * published settings. Pure: the server applies the URL's overrides before it renders, the page
 * applies the bridge's after. Callers check `appearance.allowAppOverrides` first.
 */

interface Settings {
	appearance: AgentSettings["appearance"];
	chat: AgentSettings["chat"];
	voice: AgentSettings["voice"];
	support: AgentSettings["support"];
	/** The page's copy of the assistant's name (the server keeps it in the persona). */
	assistantName?: string;
}

/** Drops the keys left undefined, so an absent override never erases a setting. */
function set<T extends object>(values: T): Partial<T> {
	return Object.fromEntries(
		Object.entries(values).filter(([, value]) => value !== undefined),
	) as Partial<T>;
}

export function applyAppearanceOverrides<T extends Settings>(
	settings: T,
	overrides: AppearanceOverrides | undefined,
): T {
	if (!overrides) {
		return settings;
	}
	const {
		starterStyle,
		showHeader,
		showLogo,
		showBasket,
		showBasketButton,
		showBasketBar,
		showCheckout,
		showNewChat,
		showFollowUps,
		showSteps,
		productSheet,
		addFromCards,
		showAssistantName,
		showStarters,
		showWelcome,
		showBack,
		showPoweredBy,
		hideOrderHelp,
		hideVoice,
		hideCall,
		fontFamily,
		...looks
	} = overrides;
	const appearance = {
		...settings.appearance,
		...set({ ...looks, deviceFont: fontFamily }),
	};
	if (looks.font || fontFamily) {
		// The app named a typeface: it replaces the retailer's uploaded one too.
		delete appearance.customFont;
	}
	return {
		...settings,
		appearance,
		chat: {
			...settings.chat,
			...set({
				starterStyle,
				showHeader,
				showLogo,
				showBasket,
				showBasketButton,
				showBasketBar,
				showCheckout,
				showNewChat,
				followUps: showFollowUps,
				showSteps,
				productSheet,
				addFromCards,
				showAssistantName,
				showStarters,
				showWelcome,
				showBack,
				// The page keeps it shown where Hephon has not allowed hiding it (poweredByLocked).
				poweredBy: showPoweredBy,
			}),
		},
		// An app can hide these, never switch on what the retailer has off.
		...(hideVoice || hideCall
			? {
					voice: {
						...settings.voice,
						...(hideVoice
							? { dictation: false, voiceNotes: false }
							: {}),
						...(hideCall ? { live: false } : {}),
					},
				}
			: {}),
		...(hideOrderHelp
			? { support: { ...settings.support, enabled: false } }
			: {}),
	};
}

/** Wording the app sent over the bridge (`text`), over the retailer's own. */
export function applyChatText<T extends Settings>(
	settings: T,
	text: ChatText,
): T {
	const { tagline, labels, assistantName, ...words } = text;
	return {
		...settings,
		...set({ assistantName }),
		appearance: { ...settings.appearance, ...set({ tagline }) },
		chat: {
			...settings.chat,
			...set(words),
			labels: { ...settings.chat.labels, ...set(labels ?? {}) },
		},
	};
}

/** The built-in wording of each replaceable label, in English. `{retailer}` is the retailer's name. */
const DEFAULT_LABELS: Record<ChatLabel, string> = COPY.en.labels;

export type ChatLabels = Record<ChatLabel, string>;

/** Every label resolved: the retailer's (or its app's) wording, else the built-in. */
export function chatLabels(
	chat: AgentSettings["chat"],
	retailerName: string,
	/** The chat's language's built-in wording (copy.ts). */
	builtIn: Record<ChatLabel, string> = DEFAULT_LABELS,
): ChatLabels {
	return Object.fromEntries(
		CHAT_LABELS.map((label) => [
			label,
			(chat.labels[label]?.trim() || builtIn[label]).replace(
				"{retailer}",
				retailerName,
			),
		]),
	) as ChatLabels;
}

export { DEFAULT_LABELS };
